import { useState } from 'react'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Mandatory Honeypot field
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error'
  const [statusMessage, setStatusMessage] = useState('')

  const validate = () => {
    const errs = {}
    if (!formData.name.trim()) {
      errs.name = 'Venligst indtast dit navn'
    }
    if (!formData.phone.trim()) {
      errs.phone = 'Venligst indtast dit telefonnummer'
    } else if (!/^[0-9+ \-()]{6,20}$/.test(formData.phone.trim())) {
      errs.phone = 'Indtast et gyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mail'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Indtast en gyldig e-mailadresse'
    }
    if (!formData.message.trim()) {
      errs.message = 'Venligst skriv en kort beskrivelse eller dit ønske'
    }
    return errs
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Honeypot check: If bot filled this, pretend success silently
    if (formData.website_url) {
      setSubmitStatus('success')
      setStatusMessage('Tak for din henvendelse! Vi vender tilbage snarest.')
      return
    }

    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
          website_url: formData.website_url // Mandatory honeypot
        })
      })

      if (response.ok) {
        setSubmitStatus('success')
        setStatusMessage('Mange tak for din besked! Vi vender tilbage snarest muligt.')
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        })
      } else {
        const result = await response.json().catch(() => ({}))
        setSubmitStatus('error')
        setStatusMessage(
          result.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring på +45 22 88 45 36.'
        )
      }
    } catch {
      setSubmitStatus('error')
      setStatusMessage(
        'Kunne ikke oprette forbindelse til serveren. Tjek din internetforbindelse eller ring direkte på +45 22 88 45 36.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="rounded-sm border border-white/10 bg-[#18181C] p-6 sm:p-8 md:p-10 shadow-xl">
      <div className="mb-6">
        <h3 className="font-serif text-xl sm:text-2xl text-[#F5F3EF]">
          Send en besked til Jens
        </h3>
        <p className="mt-1 text-xs sm:text-sm text-[#A8A49C]">
          Udfyld formularen for at aftale et lyt i lytterummet på Toftehøj 21 eller få rådgivning om dit HiFi projekt.
        </p>
      </div>

      {submitStatus === 'success' ? (
        <div className="rounded-sm border border-[#C87D46]/40 bg-[#1E1E24] p-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#C87D46]/20 text-[#C87D46] mb-4">
            <i className="fa-solid fa-check text-xl" aria-hidden="true" />
          </div>
          <h4 className="font-serif text-lg text-[#F5F3EF]">Beskeden er modtaget</h4>
          <p className="mt-2 text-sm text-[#E5E2DB] leading-relaxed">
            {statusMessage}
          </p>
          <button
            type="button"
            onClick={() => setSubmitStatus(null)}
            className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-5 py-2 text-xs font-medium text-white hover:bg-[#D98E58] transition-colors"
          >
            <span>Send en ny besked</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* Honeypot field (STRICTLY REQUIRED: sr-only + absolute positioning) */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {submitStatus === 'error' && (
            <div className="rounded-sm border border-red-500/30 bg-red-950/20 p-4 text-xs text-red-300">
              <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
              {statusMessage}
            </div>
          )}

          {/* Navn */}
          <div>
            <label htmlFor="form-name" className="block text-xs font-medium uppercase tracking-wider text-[#E5E2DB] mb-1.5">
              Navn <span className="text-[#C87D46]">*</span>
            </label>
            <input
              type="text"
              id="form-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              required
              className={`w-full rounded-sm border bg-[#121214] px-4 py-3 text-sm text-[#F5F3EF] placeholder-[#716E68] transition-colors focus:outline-none ${
                errors.name
                  ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/50'
                  : 'border-white/10 focus:border-[#C87D46] focus:ring-1 focus:ring-[#C87D46]'
              }`}
            />
            {errors.name && (
              <p className="mt-1.5 text-xs text-red-400">{errors.name}</p>
            )}
          </div>

          {/* 2-column for Phone and Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Telefonnummer */}
            <div>
              <label htmlFor="form-phone" className="block text-xs font-medium uppercase tracking-wider text-[#E5E2DB] mb-1.5">
                Telefonnummer <span className="text-[#C87D46]">*</span>
              </label>
              <input
                type="tel"
                id="form-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+45 12 34 56 78"
                required
                className={`w-full rounded-sm border bg-[#121214] px-4 py-3 text-sm text-[#F5F3EF] placeholder-[#716E68] transition-colors focus:outline-none ${
                  errors.phone
                    ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/50'
                    : 'border-white/10 focus:border-[#C87D46] focus:ring-1 focus:ring-[#C87D46]'
                }`}
              />
              {errors.phone && (
                <p className="mt-1.5 text-xs text-red-400">{errors.phone}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="form-email" className="block text-xs font-medium uppercase tracking-wider text-[#E5E2DB] mb-1.5">
                E-mail <span className="text-[#C87D46]">*</span>
              </label>
              <input
                type="email"
                id="form-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                required
                className={`w-full rounded-sm border bg-[#121214] px-4 py-3 text-sm text-[#F5F3EF] placeholder-[#716E68] transition-colors focus:outline-none ${
                  errors.email
                    ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/50'
                    : 'border-white/10 focus:border-[#C87D46] focus:ring-1 focus:ring-[#C87D46]'
                }`}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>
              )}
            </div>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="form-message" className="block text-xs font-medium uppercase tracking-wider text-[#E5E2DB] mb-1.5">
              Beskrivelse <span className="text-[#C87D46]">*</span>
            </label>
            <textarea
              id="form-message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="Fortæl om dit ønske, projekt eller om du ønsker at aftale tid til et lyt..."
              required
              className={`w-full rounded-sm border bg-[#121214] px-4 py-3 text-sm text-[#F5F3EF] placeholder-[#716E68] transition-colors focus:outline-none resize-y ${
                errors.message
                  ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/50'
                  : 'border-white/10 focus:border-[#C87D46] focus:ring-1 focus:ring-[#C87D46]'
              }`}
            />
            {errors.message && (
              <p className="mt-1.5 text-xs text-red-400">{errors.message}</p>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2.5 rounded-sm bg-[#C87D46] px-6 py-3.5 text-sm font-medium text-white shadow-lg shadow-[#C87D46]/25 hover:bg-[#D98E58] transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin text-sm" aria-hidden="true" />
                <span>Sender din besked...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                <span>Send henvendelse</span>
              </>
            )}
          </button>

          <p className="text-[11px] text-center text-[#716E68]">
            Vi behandler dine oplysninger fortroligt og bruger dem udelukkende til at besvare din henvendelse.
          </p>
        </form>
      )}
    </div>
  )
}
